import React from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';

interface ErrorProps {
  error?: Error | { message?: string };
  onRetry?: () => void;
  onClose?: () => void;
}

export const Error: React.FC<ErrorProps> = ({ error, onRetry, onClose }) => {
  const errorMessage = error?.message || 'Failed to load appointment';
  const isNotFound = errorMessage.toLowerCase().includes('not found');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 4,
        textAlign: 'center',
      }}
    >
      <ErrorOutlineIcon
        sx={{
          fontSize: 64,
          color: 'error.main',
          mb: 2,
        }}
      />

      <Typography variant="h6" gutterBottom>
        {isNotFound ? 'Appointment Not Found' : 'Unable to Load Appointment'}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
        {isNotFound
          ? "The appointment you're looking for doesn't exist or has been deleted."
          : errorMessage}
      </Typography>

      <Stack direction="row" spacing={2}>
        {!isNotFound && onRetry && (
          <Button variant="contained" onClick={onRetry} sx={{ color: 'white' }}>
            Try Again
          </Button>
        )}
        {onClose && (
          <Button
            variant={isNotFound ? 'contained' : 'outlined'}
            onClick={onClose}
            sx={isNotFound ? { color: 'white' } : {}}
          >
            Close
          </Button>
        )}
      </Stack>

      {/* Additional error details for debugging in non-production */}
      {process.env.NODE_ENV !== 'production' && error && (
        <Alert severity="error" sx={{ mt: 4, textAlign: 'left', maxWidth: 600 }}>
          <Typography variant="caption" component="pre">
            {JSON.stringify(error, null, 2)}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
